import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useProfiles } from "../profiles/ProfileContext";
import { Button } from "../components/ui/Button";
import { PageHeader } from "../components/Surface";
import { ResultCard } from "../components/ResultCard";
import { Results } from "../components/Paged";
import { ChipSelect, inputClass, labelClass } from "../components/FormKit";
import { labelize } from "../lib/format";
import {
  listJobs, payLabel, bonusLabel,
  ROUTE_TYPE_OPTIONS, CDL_CLASS_OPTIONS, JOB_EQUIPMENT_OPTIONS, HIRING_TYPES, type JobSummary,
} from "../lib/jobs";

export function JobBoard() {
  const { active } = useProfiles();
  const [params] = useSearchParams();
  const isHiring = active ? HIRING_TYPES.includes(active.type) : false;

  // Initial filters come from the URL (e.g. a marketing deep link
  // /jobs?route_type=otr or ?equipment=reefer or ?cdl_class=A) so the first
  // search is pre-filtered to what the visitor expected.
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [q, setQ] = useState(params.get("q") ?? "");
  const [location, setLocation] = useState(params.get("location") ?? "");
  const [radius, setRadius] = useState(params.get("radius") ?? "");
  const [routeType, setRouteType] = useState(params.get("route_type") ?? "");
  const [cdlClass, setCdlClass] = useState(params.get("cdl_class") ?? "");
  const [equipment, setEquipment] = useState(params.get("equipment") ?? "");
  const [loading, setLoading] = useState(true);

  async function search() {
    setLoading(true);
    try {
      setJobs(
        (await listJobs({
          q: q || undefined,
          location: location || undefined,
          radius: radius ? Number(radius) : undefined,
          route_type: routeType || undefined,
          cdl_class: cdlClass || undefined,
          equipment: equipment || undefined,
        })).jobs,
      );
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jobCard = (j: (typeof jobs)[number]) => (
    <ResultCard
      key={j.id}
      kind="jobs"
      id={j.id}
      to={`/jobs/${j.id}`}
      title={j.title}
      verified
      meta={[j.poster_name, j.location, j.distance_mi != null ? `${j.distance_mi} mi away` : null, payLabel(j.pay_min, j.pay_max, j.pay_type), j.route_type ? labelize(j.route_type) : null].filter(Boolean).join(" · ")}
      tags={[...j.equipment, j.cdl_class ? `CDL ${j.cdl_class}` : null]}
      specs={[
        ["Pay", payLabel(j.pay_min, j.pay_max, j.pay_type) ?? "—"],
        ["Route", labelize(j.route_type)],
        ["Home time", labelize(j.home_time)],
        ["Sign-on", bonusLabel(j.sign_on_bonus)],
        ["Posted", new Date(j.created_at).toLocaleDateString()],
      ]}
    />
  );

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Job board"
        subtitle="Find your next driving job — verified employers only."
        actions={
          <>
            {isHiring && (
              <>
                <Link to="/jobs/new"><Button>Post a job</Button></Link>
                <Link to="/jobs/mine"><Button variant="ghost">My postings</Button></Link>
              </>
            )}
            {active?.type === "driver" && <Link to="/applications"><Button variant="ghost">My applications</Button></Link>}
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
        {/* Filters rail */}
        <aside className="grid h-max content-start gap-4 rounded-card border border-line bg-surface p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-muted">Filters</p>
          <label className={labelClass}>
            Location
            <input className={inputClass} placeholder="City, ST" value={location} onChange={(e) => setLocation(e.target.value)} />
          </label>
          <label className={labelClass}>
            Distance
            <select className={inputClass} value={radius} onChange={(e) => setRadius(e.target.value)} aria-label="Search radius">
              <option value="">Any distance</option>
              <option value="25">Within 25 mi</option>
              <option value="50">Within 50 mi</option>
              <option value="100">Within 100 mi</option>
              <option value="250">Within 250 mi</option>
            </select>
          </label>
          <ChipSelect label="Route" options={ROUTE_TYPE_OPTIONS} value={routeType} onChange={setRouteType} />
          <ChipSelect label="CDL class" options={CDL_CLASS_OPTIONS} value={cdlClass} onChange={setCdlClass} />
          <ChipSelect label="Equipment" options={JOB_EQUIPMENT_OPTIONS.map((e) => [e, labelize(e)] as [string, string])} value={equipment} onChange={setEquipment} />
          <Button onClick={search}>Apply filters</Button>
        </aside>

        {/* Results */}
        <div className="grid content-start gap-4">
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void search();
            }}
          >
            <input className={`flex-1 ${inputClass}`} placeholder="Search by title, company, or keyword" value={q} onChange={(e) => setQ(e.target.value)} />
            <Button type="submit">Search</Button>
          </form>

          {loading ? (
            <p className="font-mono text-xs text-muted">Searching…{location ? ` · near ${location}` : ""}</p>
          ) : (
            <Results
              items={jobs}
              perPage={10}
              defaultView="list"
              gridClass="grid gap-4 sm:grid-cols-2"
              renderCard={(j) => jobCard(j)}
              renderRow={(j) => jobCard(j)}
              empty={<p className="font-mono text-sm text-muted">No open jobs match. Try widening your filters.</p>}
            />
          )}
        </div>
      </div>
    </div>
  );
}
